import type { Role } from '@/types/domain';

/**
 * Role-based access control.
 *
 * Roles are hierarchical: a higher rank implies every capability of the
 * ranks below it for routing purposes. Fine-grained capabilities are
 * declared per permission so screens can hide/disable individual actions.
 * Mirror these rules with Supabase Row-Level Security — the client guard
 * is UX, the database is the real boundary.
 */
export const ROLE_RANK: Record<Role, number> = {
  staff: 0,
  instructor: 1,
  admin: 2,
  superadmin: 3,
};

/** Which app section each role lands in / is allowed to open.
 *  Non-super admins are scoped to invite generation only, so they land on the
 *  invites screen (the rest of /admin is superadmin-gated in the router). */
export const ROLE_HOME: Record<Role, string> = {
  staff: '/learn',
  instructor: '/teach',
  admin: '/admin/invites',
  superadmin: '/admin',
};

export type Permission =
  | 'course:build'
  | 'attendance:manage'
  | 'cert:approve'
  | 'signup:approve'
  | 'org:manage'
  | 'cert:verify'
  | 'reports:view'
  | 'user:manage'
  | 'role:assign-admin';

const PERMISSIONS: Record<Permission, Role[]> = {
  'course:build': ['instructor', 'admin', 'superadmin'],
  'attendance:manage': ['instructor', 'admin', 'superadmin'],
  'cert:approve': ['instructor', 'admin', 'superadmin'],
  'signup:approve': ['admin', 'superadmin'],
  'org:manage': ['admin', 'superadmin'],
  'cert:verify': ['admin', 'superadmin'],
  'reports:view': ['admin', 'superadmin'],
  'user:manage': ['admin', 'superadmin'],
  // Only a Super Admin can assign Admin or Super Admin roles.
  'role:assign-admin': ['superadmin'],
};

export function can(role: Role | undefined, permission: Permission): boolean {
  if (!role) return false;
  return PERMISSIONS[permission].includes(role);
}

/** True when `role` ranks at or above `min`. Used to gate route sections. */
export function atLeast(role: Role | undefined, min: Role): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[min];
}

/** Which roles a given actor is allowed to assign to others. */
export function assignableRoles(actor: Role | undefined): Role[] {
  if (actor === 'superadmin') return ['staff', 'instructor', 'admin', 'superadmin'];
  if (actor === 'admin') return ['staff', 'instructor'];
  return [];
}
